import config from "config";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  IAggregate,
  ICountryEntity,
  IFundingRoundEntity,
  IInvestorEntity,
  IRoundEntity,
  VariableType,
} from "types";
import { useCheckIsAdmin, useCheckPremium, useCheckSuper } from "utils";
import { ROUND_FIELDS } from "./Round.constants";
import { ENTITY_STATUSES } from "const";
import { useRef, useState } from "react";

const GET_ROUNDS = gql`
  query getFundingRounds(
    $where: FundingRoundWhere, 
    $sort: [FundingRoundSort!], 
    $limit: Int, 
    $offset: Int, 
    $isAdmin: Boolean!, 
    $isAuth: Boolean!
    #$phrase: String!
  ) {
    fundingRoundsAggregate(
      where: $where
    ) {
      count
      __typename
    }
    fundingRounds(
      options: {limit: $limit, offset: $offset, sort: $sort}
      where: $where
    ) {
      ${ROUND_FIELDS}
    }
    countries @include(if: $isAuth) {
      name
      __typename
    }
    cityList @include(if: $isAuth)
    investors: entities (where: {
      verticalProfiles_SOME: {
        vertical: ${config.VERTICAL},
        entityTypeProfiles_SOME: {
          entityType: INVESTOR,
        }
      },
    }) @include(if: $isAuth) {
      name
    }
  }
`;

export function useGraphQLFundingRounds({
  limit,
  offset,
  sort,
  where,
  search,
}: VariableType) {
  const isAdmin = useCheckIsAdmin();
  const isPremium = useCheckPremium();
  const { loading, error, data } = useQuery<
    {
      fundingRounds: Array<IRoundEntity>;
      fundingRoundsAggregate: IAggregate<{}>;
      countries: Partial<ICountryEntity>[];
      cityList: string[];
      investors: Array<IInvestorEntity>;
    },
    VariableType
  >(GET_ROUNDS, {
    variables: {
      limit: limit,
      where: {
        ...where,
        vertical: config.VERTICAL,
        OR: [{ isGovernment: false }, { isGovernment: null }],
      },
      sort: sort,
      offset: offset,
      // phrase: search
      //   ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
      //   : "*",
      isAdmin: isAdmin,
      isAuth: isPremium,
    },
  });

  return { loading, error, data };
}

export function useRoundsExportQuery({ sort, where, search }: VariableType) {
  const isAdmin = useCheckIsAdmin();
  const isSuper = useCheckSuper();
  const isPremium = useCheckPremium();

  const exportDataRef = useRef<IRoundEntity[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [getCount] = useLazyQuery(GET_ROUNDS_COUNT);

  const [getRounds] = useLazyQuery<{ fundingRounds: IRoundEntity[] }>(
    GET_ROUNDS_EXPORT
  );

  const getExportData = async () => {
    setIsFetching(true);

    try {
      const { data: countResult } = await getCount({
        variables: {
          where: {
            ...where,
            vertical: config.VERTICAL,
            OR: [{ isGovernment: false }, { isGovernment: null }],
            ...(!isSuper &&
              !isAdmin && {
                status_IN: ENTITY_STATUSES.APPROVED,
              }),
          },
          phrase: search
            ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
            : "*",
          isAdmin: isAdmin,
          isAuth: isPremium,
        },
      });

      const isPrivilegedUser = isAdmin || isSuper;

      const totalRounds = isPrivilegedUser
        ? countResult?.fundingRoundsAggregate?.count
        : Math.min(200, countResult?.fundingRoundsAggregate?.count || 0);

      if (totalRounds === 0) {
        setIsFetching(false);
        return;
      }

      const LIMIT = !isPrivilegedUser ? totalRounds : 500;
      const totalRequests = Math.ceil(totalRounds / LIMIT);

      let accumulatedData: IRoundEntity[] = [];

      for (let i = 0; i < totalRequests; i++) {
        const { data } = await getRounds({
          variables: {
            limit: LIMIT,
            offset: i * LIMIT,
            where: {
              ...where,
              vertical: config.VERTICAL,
              OR: [{ isGovernment: false }, { isGovernment: null }],
              ...(!isSuper &&
                !isAdmin && {
                  status_IN: ENTITY_STATUSES.APPROVED,
                }),
            },
            sort: sort,
            phrase: search
              ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
              : "*",
            isAdmin: isAdmin,
            isAuth: isPremium,
          },
        });

        if (data?.fundingRounds) {
          accumulatedData = [...accumulatedData, ...data.fundingRounds];
        }
      }

      exportDataRef.current = accumulatedData;
    } catch (err) {
      console.error(err);
      setError(err as Error);
    } finally {
      setIsFetching(false);
    }
  };

  return {
    loadingExport: isFetching,
    dataExport: exportDataRef.current,
    error,
    getExportData,
  };
}

const GET_ROUNDS_COUNT = gql`
  query getRoundsCount($where: FundingRoundWhere) {
    fundingRoundsAggregate(where: $where) {
      count
    }
  }
`;

const GET_ROUNDS_EXPORT = gql`
  query getRoundsExport(
    $where: FundingRoundWhere, 
    $sort: [FundingRoundSort!], 
    $limit: Int, 
    $offset: Int, 
    $isAdmin: Boolean!, 
    $isAuth: Boolean!
  ) {
    fundingRounds (
      options: {
        limit: $limit,
        offset: $offset,
        sort: $sort
      },
      where: $where
      ) {
      ${ROUND_FIELDS}
    }
  }
`;

export function useGraphQLFundingRoundsAggregated() {
  const isAdmin = useCheckIsAdmin();
  const isSuper = useCheckSuper();

  const { loading, error, data } = useQuery<
    {
      latestFundingRound: Array<IRoundEntity>;
      InvestorCount: IAggregate<{}>;
      OrganizationCount: IAggregate<{}>;
      fundingRoundsSum;
    },
    VariableType & { status_IN?: string[] }
  >(
    gql`
    query getFundingRoundsAggregated (
      $status_IN: [Status]
    ) {
      fundingRoundsSum
      latestFundingRound: fundingRounds(
        options: {limit: 1, sort: { startDate: DESC }}
        where: { 
          startDate_GTE: "1900-01-01",
          vertical: ${config.VERTICAL},
          status_IN: $status_IN 
        }
      ) {
        id
        startDate
        status
        vertical
      }
      InvestorCount: entitiesAggregate(
        where: {
          verticalProfiles_SOME: {
            vertical: ${config.VERTICAL},
            entityTypeProfiles_SOME: {
              entityType: INVESTOR,
              status_IN: $status_IN 
            }
          }
        }
      ) {
        count
      }
      OrganizationCount: entitiesAggregate(
        where: {
          verticalProfiles_SOME: {
            vertical: ${config.VERTICAL},
            entityTypeProfiles_SOME: {
              entityType: ORGANIZATION,
              status_IN: $status_IN 
            }
          }
        }
      ) {
        count
      }
    }
  `,
    {
      variables: {
        ...(!isSuper &&
          !isAdmin && {
            status_IN: [ENTITY_STATUSES.APPROVED],
          }),
      },
    }
  );

  return { loading, error, data };
}

export function useSoftDeleteRounds() {
  return useMutation<{
    updateFundingRounds: {
      fundingRounds: Partial<IFundingRoundEntity>[];
    };
  }>(
    gql`
      mutation updateFundingRounds($ids: [ID!]) {
        updateFundingRounds(
          where: { id_IN: $ids }
          update: { status: DELETED }
        ) {
          fundingRounds {
            id
            status
          }
        }
      }
    `,
    { refetchQueries: ["getFundingRounds"] } //  TODO - MOVE NAMES OF QUERIES TO ONE ENUM
  );
}

export function useRestoreRounds() {
  return useMutation<{
    updateFundingRounds: {
      fundingRounds: Partial<IFundingRoundEntity>[];
    };
  }>(
    gql`
      mutation updateFundingRounds($ids: [ID!]) {
        updateFundingRounds(
          where: { id_IN: $ids }
          update: { status: APPROVED }
        ) {
          fundingRounds {
            id
            status
          }
        }
      }
    `,
    { refetchQueries: ["getFundingRounds"] } //  TODO - MOVE NAMES OF QUERIES TO ONE ENUM
  );
}

export function useDeleteFundingRounds() {
  return useMutation<
    { relationshipsDeleted: number | undefined },
    {
      ids: string[];
    }
  >(
    gql`
      mutation deleteFundingRounds($ids: [ID!]!) {
        deleteFundingRounds(where: { id_IN: $ids }) {
          relationshipsDeleted
        }
      }
    `,
    { refetchQueries: ["getFundingRounds"] } //  TODO - MOVE NAMES OF QUERIES TO ONE ENUM
  );
}
